<route lang="json">
{
  "name": "tools"
}
</route>

<script lang="ts">
export default {
  name: "ToolsPage",
};
</script>

<script setup lang="ts">
import BaseAlert from "@/components/Base/Alert.vue";
import BaseButton from "@/components/Base/Button.vue";
import LegacyBrowserBanner from "@/components/LegacyBrowserBanner.vue";
import MainFobizzNavbar from "@/components/MainFobizzNavbar/index.vue";
import MainHeadings from "@/components/MainHeadings.vue";
import MarketingTeaser from "@/components/MarketingTeaser.vue";
import ServerStatusBanner from "@/components/ServerStatusBanner.vue";
import TheLayout from "@/components/TheLayout.vue";
import StartpageCollections from "@/components/startpage/Collections.vue";
import StartpageCtaBannerAiDSGVOCompliant from "@/components/startpage/CtaBannerAiDSGVOCompliant.vue";
import StartpageCtaBannerGetReadyForSchool from "@/components/startpage/CtaBannerGetReadyForSchool.vue";
import StartpageCtaBannerSimpleAndSecure from "@/components/startpage/CtaBannerSimpleAndSecure.vue";
import StartpageFeaturedSection from "@/components/startpage/FeaturedSection.vue";
import StartpageRecentlyCreatedItems from "@/components/startpage/RecentlyCreatedItems.vue";
import StartpageSchoolClasses from "@/components/startpage/SchoolClasses.vue";
import StartpageToolsSection from "@/components/startpage/ToolsSection.vue";
import type {
  Item,
  PaginatedSchoolClasses,
  Collection,
} from "@/services/fobizzToolsApi";
import type { TDuplicateActionJobResponse } from "@/types";
import { v4 as uuidv4 } from "uuid";

const { t } = useI18n();
const userStore = useUserStore();
const international = computed(() => userStore.user?.international);

const { useIndexQuery: useSchoolClassIndexQuery } = useSchoolClassQueries();
const { useItemsQuery } = useStartPageQueries();
const { useListFeaturedCollectionsQuery } = useCollectionsQueries();

const router = useRouter();

const currentTabId = ref();

onMounted(() => {
  currentTabId.value = uuidv4();
});

useJobStatusChannel({
  onUpdate: async (data, json) => {
    const tabId = (json as TDuplicateActionJobResponse).tabId;

    if (
      data.status === "completed" &&
      data.path &&
      tabId === currentTabId.value
    ) {
      await router.push({ path: data.path });
    }
  },
});

const { fetch: prefetch } = usePrefetch();

const { data: schoolClasses } = useSchoolClassIndexQuery(
  {},
  {
    initialData: prefetch<PaginatedSchoolClasses>("school_classes"),
    enabled: userStore.isAuthenticated,
  }
);

const { data: collections } = useListFeaturedCollectionsQuery(
  { limit: 3, international: international },
  {
    initialData: prefetch<Collection[]>("featured_collections"),
  }
);

const { data: recentlyCreatedItems } = useItemsQuery({
  initialData: prefetch<Item[]>("recently_created_items"),
  enabled: userStore.isAuthenticated,
});
</script>

<template>
  <TheLayout :with-main-margin="false">
    <div class="startpage">
      <MainFobizzNavbar />
      <div id="main-content" class="startpage__body">
        <MarketingTeaser area="tools" class="mb-4" />
        <ServerStatusBanner />
        <LegacyBrowserBanner />
        <div
          v-if="userStore.isAuthenticated"
          class="d-flex justify-content-between align-items-start"
        >
          <MainHeadings
            :title="t('startPage.tools.headers.title')"
            :subtitle="t('startPage.tools.headers.subtitle')"
          />
        </div>
        <section v-else class="section">
          <StartpageCtaBannerSimpleAndSecure />
        </section>

        <BaseAlert
          v-if="
            userStore.isAuthenticated &&
            userStore.isFeatureEnabled('startpage_redesign')
          "
          state="light"
          :close="true"
          :title="t('startPage.headers.newStartpage')"
          class="mb-6"
        >
          <div v-safe-html="t('startPage.paragraphs.newStartpage')"></div>

          <template #actions>
            <BaseButton
              rounded
              size="sm"
              class="mt-auto"
              :to="{ name: 'home' }"
            >
              {{ t("startPage.links.newStartpage") }}
            </BaseButton>
          </template>

          <template #second-column>
            <img
              src="@/images/woman_with_gift.svg"
              class="alert-image d-none d-lg-block"
              alt="Woman with gift"
            />
          </template>
        </BaseAlert>

        <section
          v-if="
            userStore.isAuthenticated &&
            !userStore.isFeatureEnabled('startpage_redesign')
          "
          class="section startpage__two-column-section"
        >
          <StartpageRecentlyCreatedItems
            :recently-created-items="recentlyCreatedItems"
          />

          <StartpageSchoolClasses
            v-if="schoolClasses"
            :school-classes="schoolClasses.collection"
          />
        </section>
        <StartpageFeaturedSection />
        <StartpageToolsSection />
        <template v-if="!userStore.isFeatureEnabled('startpage_redesign')">
          <section v-if="collections" id="anchor-collections" class="section">
            <StartpageCollections
              :collections="collections"
              :with-link-tile="true"
            />
          </section>
          <section
            v-if="userStore.isAuthenticated && !userStore.user?.international"
            class="section"
          >
            <StartpageCtaBannerGetReadyForSchool />
          </section>
          <section v-else class="section">
            <StartpageCtaBannerAiDSGVOCompliant
              :international="userStore.user?.international"
            />
          </section>
        </template>
      </div>
    </div>
  </TheLayout>
</template>

<style lang="scss" scoped>
.startpage {
  &__body {
    & .section {
      scroll-margin-top: 6rem;
      margin-bottom: $spacer-6;
    }
  }

  &__two-column-section {
    display: flex;
    gap: $spacer-4;

    > * {
      flex: 0 0 calc(50% - ($spacer-4 / 2));
      max-width: calc(50% - ($spacer-4 / 2));
    }

    @include media-breakpoint-down(lg) {
      display: flex;
      flex-direction: column;

      > * {
        max-width: 100%;
      }
    }
  }

  &__feedback-banner {
    width: 100%;
    background-color: $color-surface-white;
    padding: $spacer-5 0;
    font-size: $font-size-sm;

    p {
      color: $gray-600;
    }

    a {
      color: $gray-600;
      text-decoration: none;
    }

    @include media-breakpoint-down(md) {
      & .container {
        padding: $spacer-2 $spacer-3;
      }
    }
  }
}

.alert-image {
  width: 170px;
  max-width: 200px;
  height: auto;
  margin-bottom: -$spacer;
}
</style>
